import { APP_LOCALE_NAME_DEFAULT, APP_LOCALES } from '@ricoh/ria.client.core/constants/core/app';
import { getData } from '@ricoh/ria.client.core/plugins/swr/common';
import { createFetcher } from '@ricoh/ria.client.core/plugins/swr/fetcher';

import type { InternalUserProfile } from '~/common/model/identity';

import { HOME_PAGES } from '~/common/constants/feature/preferences/profile';
import { prepareGroups } from '~/services/server/admin/group/mapper';
import { prepareBatchTypes } from '~/services/server/api/batch-type/mapper';
import axiosPlugin from '~/plugins/axios';

import type { UserProfileResponse } from './_fetcher.type';
import type { EditUserProfileRequestData } from './_request.type';

import requestConfig from './_request';

const { options, endpoints, createError } = requestConfig;

const client = axiosPlugin.createClient(options);

/** GetUserProfile fetcher. */
export const getUserProfileFetcher = createFetcher<void, InternalUserProfile>(endpoints.getUserProfile.key, async () => {
  try {
    const { language, homePage, batchTypes, groups, ...userInfo } = getData<UserProfileResponse>(await client.request(endpoints.getUserProfile()));

    return {
      ...userInfo,
      language: APP_LOCALES.includes(language) ? language : APP_LOCALE_NAME_DEFAULT,
      homePage: HOME_PAGES.includes(homePage) ? homePage : 'batch',
      batchTypes: prepareBatchTypes(batchTypes ?? {}),
      groups: prepareGroups(groups ?? {}),
    };
  } catch (exception) {
    throw createError('getUserProfile', 'Failed to get user profile.', exception);
  }
});

/** EditUserProfile fetcher. */
export const editUserProfileFetcher = createFetcher<EditUserProfileRequestData>(endpoints.editUserProfile.key, async (requestData) => {
  try {
    await client.request(endpoints.editUserProfile(requestData));
  } catch (exception) {
    throw createError('editUserProfile', 'Failed to edit user profile.', exception);
  }
});

/** EditUserAvatar fetcher. */
export const editUserAvatarFetcher = createFetcher<File>(endpoints.editUserAvatar.key, async (avatar) => {
  try {
    await client.request(endpoints.editUserAvatar(avatar));
  } catch (exception) {
    throw createError('editUserAvatar', 'Failed to edit user avatar.', exception);
  }
});
